import type {
  EpicState,
  EpicKind,
  ListEpicsResponse,
  ErrorResponse,
  EpicDetail,
  GetTranscriptResponse,
  TranscriptView,
  CostSummaryResponse,
  PostEpicActionRequest,
  PostEpicActionResponse,
  PostStoryActionRequest,
  PostStoryActionResponse,
  PlanManifest,
  PlanManifestResponse,
  CreateEpicRequest,
  CreateEpicResponse,
  EpicPlanView,
  PostPRReviewRefreshResponse,
  PostPRResolutionResponse,
} from '../../../../shared/types/epics';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

/**
 * Query parameters accepted by GET /api/v1/epics.
 *
 * Defined locally — the previous `ListEpicsQuery` export in
 * shared/types/epics.ts was removed in epic-99's refactor (it wasn't
 * used by the backend's Zod schema; it was only the camelCase contract
 * for this client). Inlining keeps the API client self-contained.
 */
export interface ListEpicsQuery {
  state?: EpicState;
  repo?: string;
  kind?: EpicKind;
  limit?: number;
  cursor?: string;
  /** FORGE-159: include archived + auto-hidden epics ("Show archived" toggle). */
  includeArchived?: boolean;
}

/**
 * Fetch a page of epics from the backend.
 *
 * Auth: injects Cognito JWT bearer token from auth context (localStorage in phase 1).
 * Errors: throws Error with the backend-supplied message so TanStack Query can catch.
 */
export async function fetchEpics(
  params: ListEpicsQuery = {}
): Promise<ListEpicsResponse> {
  const queryParams = new URLSearchParams();

  if (params.state) queryParams.append('state', params.state);
  if (params.repo) queryParams.append('repo', params.repo);
  if (params.kind) queryParams.append('kind', params.kind);
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.includeArchived) queryParams.append('includeArchived', 'true');

  const queryString = queryParams.toString();
  const url = `${API_BASE}/epics${queryString ? `?${queryString}` : ''}`;

  const response = await authFetch(url, { method: 'GET' });

  if (!response.ok) {
    let message = `Failed to fetch epics: ${response.statusText}`;
    try {
      const errorBody = (await response.json()) as ErrorResponse;
      if (errorBody?.error?.message) {
        message = errorBody.error.message;
      }
    } catch {
      /* response body was not JSON; fall back to statusText */
    }
    throw new Error(message);
  }

  return (await response.json()) as ListEpicsResponse;
}

/**
 * Fetch a single epic with all its stories.
 *
 * Auth: injects Cognito JWT bearer token from auth context (localStorage in phase 1).
 * Errors: throws Error('NOT_FOUND') for 404, otherwise throws with backend message.
 */
export async function fetchEpicDetail(epicId: string): Promise<EpicDetail> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}`;
  const response = await authFetch(url, { method: 'GET' });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('NOT_FOUND');
    }
    let message = `Failed to fetch epic: ${response.statusText}`;
    try {
      const errorBody = (await response.json()) as ErrorResponse;
      if (errorBody?.error?.message) {
        message = errorBody.error.message;
      }
    } catch {
      /* response body was not JSON */
    }
    throw new Error(message);
  }

  const body = (await response.json()) as { data: EpicDetail };
  return body.data;
}

/**
 * FORGE-276 M1: the runtime-strangler cutover board for an epic — per-route
 * state (monolith → shadow → canary → cut-over → retired) + rollup. Empty
 * `routes` for a non-runtime-strangler epic; the board renders nothing then.
 */
export interface CutoverRouteView {
  route: string;
  method: string;
  path: string;
  owner: string | null;
  state: 'monolith' | 'shadow' | 'canary' | 'cut-over' | 'retired';
  parity: { shadow_runs: number; shadow_matched: number; last_result: string | null };
  updated_at: string | null;
}
export interface CutoverBoardView {
  routes: CutoverRouteView[];
  summary: { total: number; by_state: Record<string, number>; progress: number; migrated: number };
}
export async function fetchEpicCutover(epicId: string): Promise<CutoverBoardView> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}/cutover`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch cutover board: ${response.statusText}`);
  }
  const body = (await response.json()) as { data: CutoverBoardView };
  return body.data;
}

/**
 * FORGE-407: what Forge did after the deploy — integration tests, CloudWatch log
 * review (MCP calls, verdict, findings) and runtime fix loops. Empty `runs` for an
 * epic that never reached post-deploy; the card renders nothing then.
 */
export type { PostDeployView, PostDeployRun, PostDeployEvent } from '../../../../shared/types/postDeploy';
export async function fetchEpicPostDeploy(epicId: string): Promise<import('../../../../shared/types/postDeploy').PostDeployView> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}/post-deploy`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) throw new Error(`Failed to fetch post-deploy checks: ${response.statusText}`);
  const body = (await response.json()) as { data: import('../../../../shared/types/postDeploy').PostDeployView };
  return body.data;
}

/**
 * S-series: fetch the security-scan findings for an epic's repo — FINDING# rows
 * + by-severity/status rollup. Empty `findings` when the repo hasn't been
 * scanned; the dashboard renders nothing then.
 */
export interface FindingView {
  fingerprint: string;
  tool: string;
  rule: string | null;
  cve: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status: 'open' | 'fixed' | 'suppressed' | 'accepted';
  file: string | null;
  line: number | null;
  message: string;
  first_seen: string | null;
  last_seen: string | null;
}
export interface FindingsView {
  findings: FindingView[];
  summary: { total: number; open: number; by_severity: Record<string, number>; by_status: Record<string, number> };
}
export async function fetchEpicFindings(epicId: string): Promise<FindingsView> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}/findings`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) throw new Error(`Failed to fetch findings: ${response.statusText}`);
  const body = (await response.json()) as { data: FindingsView };
  return body.data;
}

/**
 * Diagnostics — surfaced swallowed errors for an epic's repo (DIAG# rows that
 * used to live only in CloudWatch). Empty when nothing was swallowed.
 */
export interface DiagEventView {
  id: string;
  level: 'error' | 'warn' | 'info';
  context: string;
  message: string;
  epic: string | null;
  story_id: string | null;
  created_at: string | null;
}
export interface DiagnosticsView {
  events: DiagEventView[];
  summary: { total: number; by_level: Record<string, number> };
}
export async function fetchEpicDiagnostics(epicId: string): Promise<DiagnosticsView> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}/diagnostics`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) throw new Error(`Failed to fetch diagnostics: ${response.statusText}`);
  const body = (await response.json()) as { data: DiagnosticsView };
  return body.data;
}

/**
 * FORGE-181: fetch the epic plan (per-story specs / PDR) from S3-backed
 * plan.json. Throws `Error('NOT_FOUND')` when no plan is stored (epic predates
 * the feature, or hasn't been /proceed'd yet).
 */
export async function fetchEpicPlan(epicId: string): Promise<EpicPlanView> {
  const url = `${API_BASE}/epics/${encodeURIComponent(epicId)}/plan`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    let message = `Failed to fetch plan: ${response.statusText}`;
    try {
      const errorBody = (await response.json()) as ErrorResponse;
      if (errorBody?.error?.message) message = errorBody.error.message;
    } catch { /* not JSON */ }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: EpicPlanView };
  return body.data;
}

/**
 * Fetch one agent transcript (S3 checkpoint JSON) for a story + agent
 * inside an epic. Throws `Error('NOT_FOUND')` when the checkpoint doesn't
 * exist; everything else propagates the backend message.
 */
export async function fetchEpicTranscript(args: {
  epicId: number | string;
  storyId: string;
  agentType: string;
  /**
   * FORGE-194: the run's ECS task ARN (from its SESSION row). When provided the
   * backend resolves THAT run's checkpoint so each retry downloads its own
   * transcript; omitted → the shared "latest" pointer (backward compatible).
   */
  taskArn?: string | null;
}): Promise<TranscriptView> {
  // FORGE-131: cross-repo story ids are compound ("SecureNX/dataplus#0") but the
  // agent stores the checkpoint under the BARE story index ("0"). Send the bare
  // index so (a) the URL path carries no '/' and (b) it matches the S3 key.
  // Single-repo ids ("0") and named rows ("xgraph") are unaffected.
  const bareStoryId = String(args.storyId).includes('#')
    ? String(args.storyId).slice(String(args.storyId).lastIndexOf('#') + 1)
    : String(args.storyId);
  const qs = args.taskArn ? `?taskArn=${encodeURIComponent(args.taskArn)}` : '';
  const url =
    `${API_BASE}/epics/${encodeURIComponent(String(args.epicId))}` +
    `/transcripts/${encodeURIComponent(bareStoryId)}/${encodeURIComponent(args.agentType)}${qs}`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    let message = `Failed to load transcript: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as GetTranscriptResponse;
  return body.data;
}

/**
 * POST /api/v1/epics — create a GitHub issue Forge will pick up.
 * Returns the new issue number + URL; for single-repo + bugfix the
 * `expectedEpicPk` is also set so the caller can poll for it to appear.
 */
export async function createEpic(req: CreateEpicRequest): Promise<CreateEpicResponse['data']> {
  const response = await authFetch(`${API_BASE}/epics`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    let message = `Failed to create epic: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as CreateEpicResponse;
  return body.data;
}

/**
 * GET /api/v1/epics/:org/:repo/:epicId/plan-manifest — parse the
 * planner's story-point manifest from the epic's GitHub comments.
 * Returns `{ found: false }` when planning hasn't posted a manifest yet.
 */
export async function fetchPlanManifest(
  org: string,
  repo: string,
  epicId: number | string,
): Promise<PlanManifest> {
  const url =
    `${API_BASE}/epics/${encodeURIComponent(org)}/${encodeURIComponent(repo)}` +
    `/${encodeURIComponent(String(epicId))}/plan-manifest`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    let message = `Failed to load plan manifest: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as PlanManifestResponse;
  return body.data;
}

// ---------------------------------------------------------------------------
// Cost summary + operator actions (FORGE-039 bug fix dashboard)
// ---------------------------------------------------------------------------

export async function fetchCostSummary(
  org: string,
  repo: string,
): Promise<CostSummaryResponse['data']> {
  const url = `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/cost-summary`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to load cost summary: ${response.statusText}`);
  }
  const body = (await response.json()) as CostSummaryResponse;
  return body.data;
}

/**
 * POST /api/v1/epics/:epicId/stories/:storyId/actions — fire `/retry`,
 * `/restart`, `/abort`, or `/unblock` on the story's GitHub issue.
 */
export async function postStoryAction(
  epicId: number | string,
  storyId: string,
  req: PostStoryActionRequest,
): Promise<PostStoryActionResponse['data']> {
  // storyId is passed in the body (not the URL) — ids like "SecureNX/dataplus#0"
  // contain slashes that API Gateway decodes before Lambda sees them, splitting
  // the path and causing a 404.
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/stories/actions`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify({ ...req, storyId }),
  });
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // HTML response = route not found (CloudFront SPA fallback or stale backend).
    throw new Error(`Story action endpoint not available (HTTP ${response.status}). Deploy the latest backend first.`);
  }
  if (!response.ok) {
    let message = `Failed to send story action: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as PostStoryActionResponse;
  return body.data;
}

/**
 * POST /api/v1/epics/:id/actions — fire `/abort`, `/retry`, or `/proceed`
 * as a GitHub issue comment under the orchestrator's bot identity.
 */
// FORGE-116 #6: in-portal diff viewer.
export interface EpicDiffFile {
  filename: string;
  status: string;
  additions: number;
  deletions: number;
  changes: number;
  patch?: string;
  truncated?: boolean;
}
// FORGE-226: one diff per repo an epic touches (cross-repo epics touch many).
export interface EpicRepoDiff {
  repo: string;
  base: string;
  head: string;
  /** GitHub compare link (or branch tree link when the compare failed). */
  compareUrl: string;
  status: string;
  aheadBy: number;
  behindBy: number;
  totalCommits: number;
  totalAdditions: number;
  totalDeletions: number;
  fileCount: number;
  files: EpicDiffFile[];
  /** Per-repo failure (missing branch, app not installed); null when OK. */
  error: string | null;
  /** 'branch' = live epic branch; 'pr' = rebuilt from the pull request (works after
   *  merge / branch deletion); 'recorded' = from a SHA Forge stored on the epic. */
  source?: 'branch' | 'pr' | 'recorded';
  merged?: boolean;
  mergedAt?: string | null;
  prUrl?: string | null;
}
export interface EpicDiff {
  repos: EpicRepoDiff[];
  /** Aggregate across all repos. */
  fileCount: number;
  totalAdditions: number;
  totalDeletions: number;
  generatedAt: string;
}

export async function fetchEpicDiff(epicId: number | string, storyId?: string): Promise<EpicDiff> {
  const qs = storyId ? `?story=${encodeURIComponent(storyId)}` : '';
  const r = await authFetch(`${API_BASE}/epics/${encodeURIComponent(String(epicId))}/diff${qs}`);
  if (!r.ok) {
    let message = `Failed to load diff: ${r.statusText}`;
    try { const b = (await r.json()) as ErrorResponse; if (b?.error?.message) message = b.error.message; } catch { /* */ }
    throw new Error(message);
  }
  return ((await r.json()) as { data: EpicDiff }).data;
}

export async function postEpicAction(
  epicId: number | string,
  req: PostEpicActionRequest,
): Promise<PostEpicActionResponse['data']> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/actions`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    let message = `Failed to send action: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as PostEpicActionResponse;
  return body.data;
}

export interface AssessmentReportResult {
  reportUrl: string | null;
  htmlUrl: string;
  summary: unknown;
  commit: unknown;
}

/**
 * "Generate PDF Report" button on a migration/safety-net epic — renders the
 * Forge Assessment pack on demand and returns short-lived (15 min) presigned
 * download links. Can take up to ~100s on a cold run (headless-Chromium
 * launch on the backend) — callers should show a spinner, not a quick toast.
 */
export async function postAssessmentReport(
  epicId: number | string,
): Promise<AssessmentReportResult> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/assessment-report`;
  const response = await authFetch(url, { method: 'POST' });
  if (!response.ok) {
    let message = `Failed to generate report: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: AssessmentReportResult };
  return body.data;
}

/**
 * Fetches a fresh download link for a report that's already been generated,
 * WITHOUT re-running the Forge Lambda (no cold Chromium launch, no re-fetch
 * from GitHub) — the report itself lives in S3 for 30 days, only the
 * presigned link expires (15 min). Returns null when nothing's been
 * generated yet (404/NO_REPORT_YET) rather than throwing, so callers can
 * treat "no report yet" as a normal, silent state.
 */
export async function getAssessmentReport(
  epicId: number | string,
): Promise<AssessmentReportResult | null> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/assessment-report`;
  const response = await authFetch(url, { method: 'GET' });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    let message = `Failed to fetch report link: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: AssessmentReportResult };
  return body.data;
}

/**
 * FORGE-302 manual trigger — "check for feedback now" on an open PR. Fetches
 * reviews/comments from GitHub directly and queues anything new; Forge's
 * existing debounce sweep (~5 min) classifies + dispatches from there, so
 * the response only reports what was queued, not whether a fix will follow.
 */
export async function postPRReviewRefresh(
  epicId: number | string,
): Promise<PostPRReviewRefreshResponse['data']> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/pr/refresh`;
  const response = await authFetch(url, { method: 'POST' });
  if (!response.ok) {
    let message = `Failed to check for feedback: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as PostPRReviewRefreshResponse;
  return body.data;
}

/**
 * FORGE-309 — the human's one-click decision on a RESOLUTION_READY PR.
 * Approve merges the proposal branch onto the PR head (code change) or posts
 * the approved recommendation (judgment call) and resumes watching; Reject
 * posts an optional reason and falls back to PR_FIX_ESCALATED. `repo` is only
 * needed to disambiguate a cross-repo epic's PRs.
 */
export async function postPRResolutionDecision(
  epicId: number | string,
  decision: 'approve' | 'reject',
  args: { prNumber: number; repo?: string | null; reason?: string },
): Promise<PostPRResolutionResponse['data']> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/pr/resolution/${decision}`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify({ prNumber: args.prNumber, repo: args.repo ?? undefined, reason: args.reason }),
  });
  if (!response.ok) {
    let message = `Failed to ${decision} resolution: ${response.statusText}`;
    try {
      const b = (await response.json()) as ErrorResponse;
      if (b?.error?.message) message = b.error.message;
    } catch { /* not JSON */ }
    throw new Error(message);
  }
  const body = (await response.json()) as PostPRResolutionResponse;
  return body.data;
}

/**
 * FORGE-159: archive an epic (hide from the default list). `reason` is required
 * by the backend when the epic isn't complete — it throws the backend message
 * (which the UI surfaces) if omitted.
 */
export async function archiveEpic(
  epicId: number | string,
  reason?: string,
): Promise<{ archived: boolean }> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/archive`;
  const response = await authFetch(url, { method: 'POST', body: JSON.stringify({ reason }) });
  if (!response.ok) {
    let message = `Failed to archive epic: ${response.statusText}`;
    try { const b = (await response.json()) as ErrorResponse; if (b?.error?.message) message = b.error.message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  return ((await response.json()) as { data: { archived: boolean } }).data;
}

export async function unarchiveEpic(epicId: number | string): Promise<{ archived: boolean }> {
  const url = `${API_BASE}/epics/${encodeURIComponent(String(epicId))}/unarchive`;
  const response = await authFetch(url, { method: 'POST', body: JSON.stringify({}) });
  if (!response.ok) {
    let message = `Failed to unarchive epic: ${response.statusText}`;
    try { const b = (await response.json()) as ErrorResponse; if (b?.error?.message) message = b.error.message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  return ((await response.json()) as { data: { archived: boolean } }).data;
}
